<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import RoomDialog from '@/components/utilities/RoomDialog.vue';
import ClassRoomGeneratedListDialog from '@/components/Dialogs/Forms/ClassRoomGeneratedListDialog.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { gradesForLevel } from '@/constants/accounts';

// Sections per grade and their advisers. High school by default; the
// elementary page passes its grades and `elementary`, which works with
// generated class records (generate once per grade, then manage students).
const props = defineProps({
  grades: { type: Array, default: null },
  elementary: { type: Boolean, default: false },
});

useSessionGuard();

// The Junior/Senior High admins see their own grades; the school-wide
// admin sees all of them.
const store = useStore();
const gradeList = props.grades ?? gradesForLevel(store.getters.activeRoleLevel);
const tabs = gradeList.map((g) => ({ id: g, name: g }));

const route = useRoute();
const router = useRouter();
const toast = useToast();
const year = computed(() => store.getters.getFilterSelected);
const isApproved = computed(() => store.state.user?.user?.isAdminApproved == 1);
// Coming back from a class list returns to the same grade.
const grade = shallowRef(gradeList.includes(route.query.grade) ? route.query.grade : gradeList[0]);

const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', `/rooms-section/${encodeURIComponent(grade.value)}`, null, { params: { schoolYearId: year.value } }),
);

// Elementary: how many class records exist for this grade and year.
const generatedCount = shallowRef(0);
async function loadGenerated() {
  if (!props.elementary) return;
  try {
    const rows = await apiRequest('get', `/rooms-section/getCountGen/${encodeURIComponent(grade.value)}/${year.value}`);
    generatedCount.value = Number(rows?.[0]?.count_gen ?? 0);
  } catch {
    generatedCount.value = 0;
  }
}
watch(
  [grade, year],
  () => {
    load();
    loadGenerated();
  },
  { immediate: true },
);

const headers = [
  { title: 'Room', key: 'room_section' },
  { title: 'Class adviser', key: 'name' },
  // Enrolled in the selected school year, the same students as the class list.
  { title: 'Students', key: 'student_count', align: 'end', width: 120 },
];

const strands = shallowRef([]);
apiRequest('get', '/rooms-section/AllStrand/Data/strand')
  .then((rows) => (strands.value = rows || []))
  .catch(() => {});

const editing = shallowRef(null);
const formOpen = shallowRef(false);
function openForm(room = null) {
  editing.value = room;
  formOpen.value = true;
}

// High school class lists have their own page; elementary uses the
// generated-list dialog (opened by handing it a fresh object).
const generatedList = shallowRef(null);
function openClassList(room) {
  if (props.elementary) {
    generatedList.value = { ...room };
    return;
  }
  router.push({
    path: `${route.path.replace(/\/$/, '')}/${room.id}/students`,
    query: { grade: grade.value, name: room.room_section },
  });
}

function printClassList(room) {
  window.open(
    `${process.env.VUE_APP_SERVER}/pdf-generator/getAllStudenList/${year.value}/${room.id}/${encodeURIComponent(grade.value)}`,
    '_blank',
  );
}

const { confirm } = useConfirm();
const { busy, run } = useAsyncAction();

async function remove(room) {
  const ok = await confirm({
    title: 'Delete room?',
    description: `${room.room_section} will be deleted. This can't be undone.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/rooms-section/${room.id}`), { success: 'Room deleted.', error: 'Could not delete the room.' }))) {
    load();
  }
}

async function generate() {
  const ok = await confirm({
    title: `Generate ${grade.value} class records?`,
    description: 'Enrolled students are placed into class records for this school year. Do this once per grade.',
    confirmText: 'Generate',
  });
  if (!ok) return;
  const check = await apiRequest('get', `/enroll-student/getSchoolYear/toGenerate/${encodeURIComponent(grade.value)}/${year.value}`).catch(() => null);
  if (!Number(check?.[0]?.conflict)) {
    toast.error('No students are enrolled in this grade for the school year.');
    return;
  }
  const done = await run(
    () => apiRequest('post', `/rooms-section/generateClassRecord/byGrade/level/${encodeURIComponent(grade.value)}/${year.value}`),
    { success: 'Class records generated.', error: 'Could not generate class records.' },
  );
  if (done) {
    load();
    loadGenerated();
  }
}

const canManageStudents = computed(() => !props.elementary || generatedCount.value > 0);
const rowActions = (room) => [
  { title: 'Class list', icon: 'mdi-account-group-outline', hidden: !canManageStudents.value, onClick: () => openClassList(room) },
  { title: 'Print class list', icon: 'mdi-printer-outline', hidden: props.elementary, onClick: () => printClassList(room) },
  { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(room) },
  { title: 'Delete', icon: 'mdi-trash-can-outline', destructive: true, hidden: !props.elementary, onClick: () => remove(room) },
];
</script>

<template>
  <UiTableCard
    v-model:tab="grade"
    title="Rooms"
    description="Sections per grade and their advisers."
    :tabs="tabs"
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search rooms"
    :empty-text="`No rooms in ${grade} yet.`"
    row-label="room_section"
    :row-actions="rowActions"
  >
    <template #actions>
      <v-btn
        v-if="elementary && isApproved && generatedCount === 0"
        variant="outlined"
        prepend-icon="mdi-database-check-outline"
        :loading="busy"
        @click="generate"
      >
        Generate class records
      </v-btn>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openForm()">Add room</v-btn>
    </template>
    <template #[`item.room_section`]="{ item }">
      <button v-if="canManageStudents" type="button" class="room-link" @click="openClassList(item)">
        {{ item.room_section }}
      </button>
      <span v-else class="font-weight-medium">{{ item.room_section }}</span>
    </template>
    <template #[`item.student_count`]="{ item }">
      <span style="font-variant-numeric: tabular-nums" :class="{ 'text-medium-emphasis': !item.student_count }">{{ item.student_count ?? '—' }}</span>
    </template>
    <template #[`item.name`]="{ item }">
      <span :class="{ 'text-medium-emphasis': !item.name }">{{ item.name || 'No class adviser' }}</span>
    </template>
  </UiTableCard>

  <RoomDialog v-model="formOpen" :room="editing" :grade="grade" :strands="strands" @saved="load" />
  <ClassRoomGeneratedListDialog v-if="elementary" :data="generatedList" action="View" :grade="grade" :filter="year" />
</template>

<style scoped>
.room-link {
  border: 0;
  padding: 0;
  background: transparent;
  font: inherit;
  font-weight: 500;
  color: var(--foreground);
  cursor: pointer;
}
.room-link:hover {
  text-decoration: underline;
}
</style>
